import type { IconName } from "@similarweb/icons";

import { translate } from "app/filters/i18nFilter";
import { minVisitsAbbrFilter, timeFilter, percentageSignFilter } from "app/filters/ngFilters";
import { numberFilter } from "app/filters/numberFilter";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { CategoryServiceUtils } from "app/services/CategoryServiceUtils";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

// Single-specific metric keys
export type SingleMetricKey =
    | "monthly-visits"
    | "unique-visitors"
    | "visit-duration"
    | "pages-per-visit"
    | "bounce-rate";

type FormatterFunction = (value: number) => string;

export interface SingleMetricConfiguration {
    id: SingleMetricKey;
    name: string;
    apiName: string;
    metricName: string;
    metric: string; // API metric name for dashboard
    family?: string; // Dashboard family
    type?: string; // Widget type for dashboard
    iconName: IconName;
    tooltip?: string;
    formatter: FormatterFunction;
    yAxisFormatter: FormatterFunction;
    tooltipFormatter: FormatterFunction;
    supportedGranularities: string[];
    getTitle: (params: { is28d: boolean }) => string;
    getEndpoint: (params: { isPeriodOverPeriod: boolean }) => string;
    getChartType: (params: { webSource: string; isPeriodOverPeriod: boolean }) => string;
    isTabDisabled?: (durations: any) => boolean;
    tooltipProps?: {
        displayTooltipTotalRow?: (params: { webSource: string }) => boolean;
    };
}

// Clean metric configurations similar to comparison/config.ts
const SINGLE_METRIC_CONFIGURATION: Record<SingleMetricKey, SingleMetricConfiguration> = {
    "monthly-visits": {
        id: "monthly-visits",
        name: "Monthly Visits",
        apiName: "Visits",
        get metricName() {
            return translate("market.research.overview.comparison.highlevel.tabs.monthlyVisits");
        },
        metric: "EngagementVisits",
        iconName: "visits",
        formatter: (value) => minVisitsAbbrFilter()(value),
        yAxisFormatter: (value) => minVisitsAbbrFilter()(value),
        tooltipFormatter: (value) => minVisitsAbbrFilter()(value),
        supportedGranularities: [GranularityNames.DAILY, GranularityNames.WEEKLY, GranularityNames.MONTHLY],
        getTitle: ({ is28d }) =>
            is28d
                ? translate("wa.ao.graph.avgvisitsdaily")
                : translate("market.research.overview.comparison.highlevel.tabs.monthlyVisits"),
        getEndpoint: ({ isPeriodOverPeriod }) =>
            isPeriodOverPeriod
                ? "widgetApi/IndustryAnalysisOverview/EngagementVisits/GraphPOP"
                : "widgetApi/IndustryAnalysisOverview/EngagementVisits/Graph",
        getChartType: ({ webSource, isPeriodOverPeriod }) =>
            isPeriodOverPeriod ? "ComparedLine" : webSource === "MobileWeb" ? "Column" : "Graph",
        tooltipProps: {
            displayTooltipTotalRow: ({ webSource }) => webSource !== "MobileWeb",
        },
    },

    "unique-visitors": {
        id: "unique-visitors",
        name: "Unique Visitors",
        apiName: "UniqueUsers",
        get metricName() {
            return translate("market.research.overview.comparison.highlevel.tabs.uniqueVisitors");
        },
        metric: "EngagementUniqueUsers",
        iconName: "monthly-unique-visitors",
        formatter: (value) => minVisitsAbbrFilter()(value),
        yAxisFormatter: (value) => minVisitsAbbrFilter()(value),
        tooltipFormatter: (value) => minVisitsAbbrFilter()(value),
        supportedGranularities: [GranularityNames.MONTHLY],
        getTitle: () => translate("market.research.overview.comparison.highlevel.tabs.uniqueVisitors"),
        getEndpoint: ({ isPeriodOverPeriod }) =>
            isPeriodOverPeriod
                ? "widgetApi/IndustryAnalysisOverview/UniqueUsers/GraphPOP"
                : "widgetApi/IndustryAnalysisOverview/UniqueUsers/Graph",
        getChartType: ({ webSource, isPeriodOverPeriod }) =>
            webSource === devicesTypes.TOTAL && !isPeriodOverPeriod ? chartTypes.AREA : chartTypes.LINE,
        isTabDisabled: (durations) => {
            const { isWindow } = durations?.forAPI || {};
            const selectedCustomIndustryHasSegments = CategoryServiceUtils.selectedCategoryHasSegments();
            return isWindow || selectedCustomIndustryHasSegments;
        },
        tooltipProps: {
            displayTooltipTotalRow: ({ webSource }) => webSource === devicesTypes.TOTAL,
        },
    },

    "visit-duration": {
        id: "visit-duration",
        name: "Avg. Visit Duration",
        apiName: "VisitDuration",
        get metricName() {
            return translate("market.research.overview.comparison.highlevel.tabs.visitDuration");
        },
        metric: "EngagementAvgVisitDuration",
        iconName: "avg-visit-duration",
        tooltip: "industry.overview.metrics.average.visit.duration.tooltip",
        formatter: (value) => timeFilter()(value, null) as string,
        yAxisFormatter: (value) => timeFilter()(value, null) as string,
        tooltipFormatter: (value) => timeFilter()(value, null) as string,
        supportedGranularities: [GranularityNames.DAILY, GranularityNames.WEEKLY, GranularityNames.MONTHLY],
        getTitle: () => translate("market.research.overview.comparison.highlevel.tabs.visitDuration"),
        getEndpoint: ({ isPeriodOverPeriod }) =>
            isPeriodOverPeriod
                ? "widgetApi/IndustryAnalysisOverview/EngagementVisitDuration/GraphPOP"
                : "widgetApi/IndustryAnalysisOverview/EngagementVisitDuration/Graph",
        getChartType: ({ webSource, isPeriodOverPeriod }) => {
            const selectedCustomIndustryHasSegments = CategoryServiceUtils.selectedCategoryHasSegments();
            return webSource === devicesTypes.TOTAL && !isPeriodOverPeriod && !selectedCustomIndustryHasSegments
                ? chartTypes.AREA
                : chartTypes.LINE;
        },
        tooltipProps: {
            displayTooltipTotalRow: ({ webSource }) => webSource === devicesTypes.TOTAL,
        },
    },

    "pages-per-visit": {
        id: "pages-per-visit",
        name: "Pages Per Visit",
        apiName: "PagesPerVisit",
        get metricName() {
            return translate("market.research.overview.comparison.highlevel.tabs.pagesPerVisit");
        },
        metric: "EngagementPagesPerVisit",
        iconName: "pages-per-visit",
        tooltip: "industry.overview.metrics.pages.per.visit.tooltip",
        formatter: (value) => numberFilter()(value, 2),
        yAxisFormatter: (value) => numberFilter()(value, 2),
        tooltipFormatter: (value) => numberFilter()(value, 2),
        supportedGranularities: [GranularityNames.DAILY, GranularityNames.WEEKLY, GranularityNames.MONTHLY],
        getTitle: () => translate("market.research.overview.comparison.highlevel.tabs.pagesPerVisit"),
        getEndpoint: ({ isPeriodOverPeriod }) =>
            isPeriodOverPeriod
                ? "widgetApi/IndustryAnalysisOverview/EngagementPagesPerVisit/GraphPOP"
                : "widgetApi/IndustryAnalysisOverview/EngagementPagesPerVisit/Graph",
        getChartType: ({ webSource, isPeriodOverPeriod }) => {
            const selectedCustomIndustryHasSegments = CategoryServiceUtils.selectedCategoryHasSegments();
            return webSource === devicesTypes.TOTAL && !isPeriodOverPeriod && !selectedCustomIndustryHasSegments
                ? chartTypes.AREA
                : chartTypes.LINE;
        },
        tooltipProps: {
            displayTooltipTotalRow: ({ webSource }) => webSource === devicesTypes.TOTAL,
        },
    },

    "bounce-rate": {
        id: "bounce-rate",
        name: "Bounce Rate",
        apiName: "BounceRate",
        get metricName() {
            return translate("market.research.overview.comparison.highlevel.tabs.bounceRate");
        },
        metric: "EngagementBounceRate",
        iconName: "bounce-rate-2",
        tooltip: "industry.overview.metrics.bounce.rate.tooltip",
        formatter: (value) => percentageSignFilter()(value, 2) as string,
        yAxisFormatter: (value) => percentageSignFilter()(value, 2) as string,
        tooltipFormatter: (value) => percentageSignFilter()(value, 2) as string,
        supportedGranularities: [GranularityNames.DAILY, GranularityNames.WEEKLY, GranularityNames.MONTHLY],
        getTitle: () => translate("market.research.overview.comparison.highlevel.tabs.bounceRate"),
        getEndpoint: ({ isPeriodOverPeriod }) =>
            isPeriodOverPeriod
                ? "widgetApi/IndustryAnalysisOverview/EngagementBounceRate/GraphPOP"
                : "widgetApi/IndustryAnalysisOverview/EngagementBounceRate/Graph",
        getChartType: ({ webSource, isPeriodOverPeriod }) => {
            const selectedCustomIndustryHasSegments = CategoryServiceUtils.selectedCategoryHasSegments();
            return webSource === devicesTypes.TOTAL && !isPeriodOverPeriod && !selectedCustomIndustryHasSegments
                ? chartTypes.AREA
                : chartTypes.LINE;
        },
        tooltipProps: {
            displayTooltipTotalRow: ({ webSource }) => webSource === devicesTypes.TOTAL,
        },
    },
};

// Helper functions similar to comparison/config.ts
export const SINGLE_METRIC_KEYS = Object.keys(SINGLE_METRIC_CONFIGURATION) as SingleMetricKey[];

export const getSingleMetricConfig = (metric: SingleMetricKey) => SINGLE_METRIC_CONFIGURATION[metric];

export const isMetricSupportedForGranularity = (selectedMetric: SingleMetricKey, timeGranularity: string): boolean => {
    const config = SINGLE_METRIC_CONFIGURATION[selectedMetric];
    return config?.supportedGranularities.includes(timeGranularity) || false;
};
